import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, Menu, X } from "lucide-react";
import api from "../lib/api";
import { Wordmark } from "./Brand";
import { COMPANY, COMPANY_URL, CONTACT_EMAIL, PRODUCT } from "./content";

const NAV = [
  ["Product", "/#features"],
  ["How it works", "/#how"],
  ["Pricing", "/#pricing"],
  ["FAQ", "/#faq"],
];

const cta = "inline-flex items-center justify-center h-10 px-4 rounded-md bg-ac-action text-white text-[14px] font-medium hover:bg-ac-action-hover transition-colors duration-fast ease-ac";

/* Sticky, on a solid background - no glass (PRD 22). Sign in and an indigo
   primary that names what the visitor can actually do. */
export const Nav = ({ signupsOpen, signedIn }) => {
  const [open, setOpen] = useState(false);
  const action = signedIn ? { to: "/app", label: "Open app" }
    : signupsOpen ? { to: "/signup", label: "Start free" }
      : { to: "/#start", label: "Join the waitlist" };
  const Action = ({ className }) => (action.to.startsWith("/#")
    ? <a href={action.to} className={className}>{action.label}</a>
    : <Link to={action.to} className={className}>{action.label}</Link>);

  return (
    <header className="fixed inset-x-0 top-0 z-50 bg-ac-bg border-b border-ac-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" aria-label={PRODUCT}><Wordmark /></Link>
        <nav className="hidden md:flex items-center gap-8" aria-label="Main">
          {NAV.map(([label, href]) => (
            <a key={href} href={href} className="text-[14px] text-ac-muted hover:text-ac-text transition-colors duration-fast">{label}</a>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-2">
          {!signedIn && <Link to="/login" className="text-[14px] text-ac-text hover:text-ac-link px-3 py-2">Sign in</Link>}
          <Action className={cta} />
        </div>
        <button className="md:hidden text-ac-text p-2 -mr-2" onClick={() => setOpen(!open)} aria-label="Menu" aria-expanded={open}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-ac-border bg-ac-bg px-4 py-3 flex flex-col">
          {NAV.map(([label, href]) => (
            <a key={href} href={href} onClick={() => setOpen(false)} className="py-2.5 text-ac-text">{label}</a>
          ))}
          {!signedIn && <Link to="/login" className="py-2.5 text-ac-text">Sign in</Link>}
          <Action className={`${cta} mt-2`} />
        </div>
      )}
    </header>
  );
};

const FOOT = [
  ["Product", [["Features", "/#features"], ["How it works", "/#how"], ["Pricing", "/#pricing"], ["FAQ", "/#faq"], ["Sign in", "/login"]]],
  ["Trust", [["Security", "/security"], ["DPA", "/dpa"], ["Status", "/status"], ["Changelog", "/changelog"]]],
  ["Company", [["Terms", "/terms"], ["Privacy", "/privacy"], [CONTACT_EMAIL, `mailto:${CONTACT_EMAIL}`]]],
];

const FootLink = ([label, href]) => (
  <li key={href}>
    {href.startsWith("/") && !href.startsWith("/#")
      ? <Link to={href} className="hover:text-ac-text">{label}</Link>
      : <a href={href} className="hover:text-ac-text">{label}</a>}
  </li>
);

export const Footer = () => (
  <footer className="border-t border-ac-border bg-ac-bg">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 py-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1fr]">
      <div>
        <Wordmark />
        <p className="mt-4 max-w-sm text-[14px] text-ac-muted leading-relaxed">
          {PRODUCT} is an AI content autopilot for brands and publishers. It reads your news feeds,
          writes SEO-ready articles with featured images for your WordPress site, turns them into
          posts for Facebook, Instagram, Threads and LinkedIn, and publishes on schedule, with an
          approval step whenever you want one.
        </p>
      </div>
      {FOOT.map(([title, links]) => (
        <div key={title}>
          <p className="mono-label text-ac-muted">{title}</p>
          <ul className="mt-4 space-y-2.5 text-[14px] text-ac-muted">{links.map(FootLink)}</ul>
        </div>
      ))}
    </div>
    <div className="border-t border-ac-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 py-6 flex flex-col sm:flex-row gap-3 sm:items-center justify-between text-[13px] text-ac-muted">
        <a href={COMPANY_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-ac-text">
          <OptimmlMark /> {PRODUCT} is a product of {COMPANY} ↗
        </a>
        <p>© {new Date().getFullYear()} {COMPANY}. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

/* A small stand-in for the Optimml logo: three slanted strokes, as on the
   company's own mark. Swap for the real SVG when it is supplied. */
const OptimmlMark = () => (
  <svg viewBox="0 0 20 16" className="w-5 h-4" aria-hidden="true">
    {[0, 5, 10].map((x) => <path key={x} d={`M${x + 2} 14 L${x + 8} 2`} stroke="#A5B4FC" strokeWidth="2" strokeLinecap="round" />)}
  </svg>
);

/* Early-access sign-up, shared by the landing page and the maintenance page. */
export const WaitlistForm = ({ compact = false }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");   // idle | busy | done | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setState("busy");
    setError("");
    try {
      await api.post("/platform/waitlist", { email });
      setState("done");
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Try again.");
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <p className="inline-flex items-center gap-2.5 rounded-md border border-[#1A4D30] bg-[#0F2A1B] px-4 py-3 text-[14px] text-[#86EFAC]">
        <Check className="w-4 h-4" /> You're on the list. We'll email you when your wave opens.
      </p>
    );
  }

  return (
    <form onSubmit={submit} className={`w-full ${compact ? "max-w-md" : "max-w-lg"}`}>
      <div className="flex flex-col sm:flex-row gap-2">
        <label className="sr-only" htmlFor="waitlist-email">Email address</label>
        <input id="waitlist-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="flex-1 h-11 rounded-md border border-ac-border bg-ac-surface px-3 text-[15px] text-ac-text placeholder:text-ac-muted/70 focus:outline-none focus:border-ac-link" />
        <button type="submit" disabled={state === "busy"}
          className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-ac-action text-white text-[15px] font-medium hover:bg-ac-action-hover disabled:opacity-70">
          {state === "busy" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join the waitlist"}
        </button>
      </div>
      {error && <p className="mt-2 text-[13px] text-[#FCA5A5]">{error}</p>}
    </form>
  );
};
